'use client';
import React, { useState, useRef, useEffect } from 'react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Edit2, Trash2, Plus, Star, TrendingUp, MoreVertical, UserX, UserCheck } from 'lucide-react';

interface Cleaner {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: 'active' | 'inactive' | 'suspended';
  jobsCompleted: number;
  jobsThisWeek: number;
  rating: number;
  totalEarned: number;
  joinedDate: string;
  specialties: string[];
}

const mockCleaners: Cleaner[] = [
  { id: 'cl-001', name: 'Maria Gonzalez', email: 'maria.g@cleaners.io', phone: '(512) 555-0142', status: 'active', jobsCompleted: 187, jobsThisWeek: 6, rating: 4.9, totalEarned: 11780, joinedDate: 'Jan 2024', specialties: ['Deep Clean', 'Move-Out'] },
  { id: 'cl-002', name: 'Marcus Johnson', email: 'marcus.j@cleaners.io', phone: '(512) 555-0287', status: 'active', jobsCompleted: 143, jobsThisWeek: 5, rating: 4.8, totalEarned: 8920, joinedDate: 'Mar 2024', specialties: ['Standard', 'Commercial'] },
  { id: 'cl-003', name: 'Devon Richards', email: 'devon.r@cleaners.io', phone: '(512) 555-0394', status: 'active', jobsCompleted: 98, jobsThisWeek: 4, rating: 4.7, totalEarned: 6020, joinedDate: 'Jun 2024', specialties: ['Standard', 'Move-In'] },
  { id: 'cl-004', name: 'Aisha Patel', email: 'aisha.p@cleaners.io', phone: '(512) 555-0451', status: 'active', jobsCompleted: 64, jobsThisWeek: 3, rating: 4.6, totalEarned: 3870, joinedDate: 'Sep 2024', specialties: ['Standard', 'Deep Clean'] },
  { id: 'cl-005', name: 'Carlos Mendez', email: 'carlos.m@cleaners.io', phone: '(512) 555-0563', status: 'inactive', jobsCompleted: 31, jobsThisWeek: 0, rating: 4.3, totalEarned: 1780, joinedDate: 'Jan 2025', specialties: ['Standard'] },
  { id: 'cl-006', name: 'Tanya Brooks', email: 'tanya.b@cleaners.io', phone: '(512) 555-0671', status: 'suspended', jobsCompleted: 12, jobsThisWeek: 0, rating: 3.8, totalEarned: 680, joinedDate: 'Mar 2025', specialties: ['Standard'] },
];

interface CleanerFormData {
  name: string;
  email: string;
  phone: string;
}

const statusVariant = (s: Cleaner['status']) => {
  if (s === 'active') return 'success';
  if (s === 'inactive') return 'neutral';
  return 'danger';
};

function CleanerRowMenu({ cleaner, onEdit, onDeactivate, onReactivate, onDelete }: {
  cleaner: Cleaner;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
        aria-label="More actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-50 w-44 bg-card border border-border rounded-xl shadow-lg py-1 overflow-hidden">
          <button
            onClick={() => { onEdit(); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
          >
            <Edit2 size={14} className="text-muted-foreground flex-shrink-0" />
            Edit Profile
          </button>
          <div className="border-t border-border my-1" />
          {cleaner.status === 'active' ? (
            <button
              onClick={() => { onDeactivate(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <UserX size={14} className="text-warning flex-shrink-0" />
              Deactivate
            </button>
          ) : (
            <button
              onClick={() => { onReactivate(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
            >
              <UserCheck size={14} className="text-success flex-shrink-0" />
              Reactivate
            </button>
          )}
          <div className="border-t border-border my-1" />
          <button
            onClick={() => { onDelete(); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-danger hover:bg-danger-bg transition-colors"
          >
            <Trash2 size={14} className="flex-shrink-0" />
            Delete Account
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminCleanerTable() {
  const [cleaners, setCleaners] = useState(mockCleaners);
  const [editCleaner, setEditCleaner] = useState<Cleaner | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Cleaner | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CleanerFormData>();

  const onSave = async (data: CleanerFormData) => {
    // Backend integration point: PUT /api/cleaners/:id or POST /api/cleaners
    await new Promise(r => setTimeout(r, 800));
    if (editCleaner) {
      setCleaners(prev => prev.map(c => c.id === editCleaner.id ? { ...c, ...data } : c));
      toast.success('Cleaner profile updated');
    } else {
      const newCleaner: Cleaner = {
        id: `cl-${Date.now()}`,
        ...data,
        status: 'active',
        jobsCompleted: 0,
        jobsThisWeek: 0,
        rating: 0,
        totalEarned: 0,
        joinedDate: 'May 2026',
        specialties: ['Standard'],
      };
      setCleaners(prev => [...prev, newCleaner]);
      toast.success('New cleaner added to platform');
    }
    setEditCleaner(null);
    setShowAdd(false);
    reset();
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setCleaners(prev => prev.filter(c => c.id !== deleteTarget.id));
    toast.success(`${deleteTarget.name} removed from platform`);
    setDeleteTarget(null);
  };

  const handleDeactivate = (id: string) => {
    setCleaners(prev => prev.map(c => c.id === id ? { ...c, status: 'inactive' as const } : c));
    toast.success('Cleaner account deactivated');
  };

  const handleReactivate = (id: string) => {
    setCleaners(prev => prev.map(c => c.id === id ? { ...c, status: 'active' as const } : c));
    toast.success('Cleaner account reactivated');
  };

  return (
    <>
      <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <p className="text-sm font-bold text-foreground">Cleaner Management</p>
            <p className="text-xs text-muted-foreground mt-0.5">{cleaners.filter(c => c.status === 'active').length} active cleaners · {cleaners.length} total</p>
          </div>
          <button
            onClick={() => { setShowAdd(true); reset(); }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150"
          >
            <Plus size={15} /> Add Cleaner
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {['Cleaner', 'Contact', 'Status', 'Rating', 'Jobs Done', 'This Week', 'Total Earned', 'Joined', 'Actions'].map(h => (
                  <th key={`clh-${h}`} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {cleaners.map((cl) => (
                <tr key={cl.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {cl.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{cl.name}</p>
                        <p className="text-xs text-muted-foreground">{cl.specialties.join(', ')}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-foreground">{cl.email}</p>
                    <p className="text-xs text-muted-foreground">{cl.phone}</p>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(cl.status)} label={cl.status.charAt(0).toUpperCase() + cl.status.slice(1)} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Star size={12} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-semibold text-foreground font-tabular">{cl.rating > 0 ? cl.rating.toFixed(1) : '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground font-tabular">{cl.jobsCompleted}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <TrendingUp size={12} className="text-primary" />
                      <span className="text-sm font-semibold text-foreground font-tabular">{cl.jobsThisWeek}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-foreground font-tabular">£{cl.totalEarned.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{cl.joinedDate}</td>
                  <td className="px-4 py-3">
                    <CleanerRowMenu
                      cleaner={cl}
                      onEdit={() => { setEditCleaner(cl); reset({ name: cl.name, email: cl.email, phone: cl.phone }); }}
                      onDeactivate={() => handleDeactivate(cl.id)}
                      onReactivate={() => handleReactivate(cl.id)}
                      onDelete={() => setDeleteTarget(cl)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit / Add Modal */}
      <Modal
        open={!!editCleaner || showAdd}
        onClose={() => { setEditCleaner(null); setShowAdd(false); reset(); }}
        title={editCleaner ? 'Edit Cleaner Profile' : 'Add New Cleaner'}
        size="md"
      >
        <form onSubmit={handleSubmit(onSave)} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name</label>
            <input
              type="text"
              className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all ${errors.name ? 'border-danger' : 'border-input'}`}
              {...register('name', { required: 'Name is required' })}
            />
            {errors.name && <p className="mt-1 text-xs text-danger">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
            <input
              type="email"
              className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all ${errors.email ? 'border-danger' : 'border-input'}`}
              {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })}
            />
            {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number</label>
            <input
              type="tel"
              className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all ${errors.phone ? 'border-danger' : 'border-input'}`}
              {...register('phone', { required: 'Phone is required' })}
            />
            {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone.message}</p>}
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setEditCleaner(null); setShowAdd(false); reset(); }}
              className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2.5 rounded-xl gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Saving...
                </>
              ) : (
                editCleaner ? 'Save Changes' : 'Add Cleaner'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Account"
        size="sm"
      >
        {deleteTarget && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-danger-bg rounded-xl border border-danger/20">
              <Trash2 size={16} className="text-danger flex-shrink-0 mt-0.5" />
              <p className="text-sm text-danger leading-relaxed">
                Delete <strong>{deleteTarget.name}</strong>'s account? This will permanently remove them from the platform and unassign any pending jobs. This cannot be undone.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2.5 rounded-xl bg-danger text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all duration-150"
              >
                Delete Account
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}